import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { environment, APP_VERSION } from '../config/environment';
import { StorageUtil } from '../utils/storage.util';

export class ApiService {
  private client: AxiosInstance;

  /**
   * Retorna headers padrão para requisições
   * Útil para casos onde axios é usado diretamente (ex: header enrichment)
   */
  static getDefaultHeaders(token?: string | null): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-APP-VERSION': APP_VERSION,
      'X-CHANNEL': 'WEB',
      'X-CONSUMER': 'VIVOREWARDS'
    };

    if (token) {
      headers['X-AUTHORIZATION'] = token;
    }

    return headers;
  }

  constructor() {
    this.client = axios.create({
      baseURL: environment.apiBaseUrl,
      headers: {
        'Content-Type': 'application/json',
        'X-APP-VERSION': APP_VERSION,
        'X-CHANNEL': 'WEB',
        'X-CONSUMER': 'VIVOREWARDS'
      }
    });

    // Interceptor para adicionar token de autenticação e headers padrão
    this.client.interceptors.request.use(
      (config) => {
        const token = StorageUtil.getToken();
        if (token) {
          config.headers['X-AUTHORIZATION'] = token;
        }
        // Garantir que headers padrão estão sempre presentes
        // Mas não sobrescrever headers específicos passados no config
        if (!config.headers['X-APP-VERSION']) {
          config.headers['X-APP-VERSION'] = APP_VERSION;
        }
        if (!config.headers['X-CHANNEL']) {
          config.headers['X-CHANNEL'] = 'WEB';
        }
        if (!config.headers['X-CONSUMER']) {
          config.headers['X-CONSUMER'] = 'VIVOREWARDS';
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor para tratamento de erros
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token inválido ou expirado
          StorageUtil.clear();
          window.location.href = '/#/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}

export const apiService = new ApiService();

